import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const LANGUAGE_DOMAINS = {
  en: 'https://www.aivhy.com',
  nl: 'https://www.aivhy.nl'
};

function inferDomainFromHost(host) {
  if (!host) return undefined;
  if (host.includes("aivhy.nl")) return LANGUAGE_DOMAINS.nl;
  if (host.includes("aivhy.com")) return LANGUAGE_DOMAINS.en;
  return undefined;
}

function getBaseUrl(currentLang, host) {
  const inferred = inferDomainFromHost(host);
  if (inferred) return inferred;
  return LANGUAGE_DOMAINS[currentLang] || LANGUAGE_DOMAINS.en;
}

function safeJsonString(obj) {
  const json = JSON.stringify(obj);
  return json
    .replace(/<\/script/gi, "<\\/script")
    .replace(/</g, "\\u003c");
}

export default function SEO({ 
  title, 
  description, 
  path = '',
  image, 
  type = 'website', // 'website' or 'article'
  noIndex = false,
  jsonLd,
  children 
}) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const host = typeof window !== "undefined" ? window.location.host : undefined;
  const baseUrl = getBaseUrl(currentLang, host);
  
  // Construct full URLs
  const normalizedPath = path ? (path.startsWith("/") ? path : `/${path}`) : "";
  const fullUrl = `${baseUrl}${normalizedPath}`;
  const fullImage = image ? (image.startsWith("http") ? image : `${baseUrl}${image}`) : `${baseUrl}/images/logos/LOGO_AIVHY.webp`;
  
  // Page title and description
  const pageTitle = title ? `${title} | AIVHY` : "AIVHY - Industry 4.0, IIoT & Cyber Security Solutions";
  const pageDescription = description || "AIVHY accelerates Industry 4.0, IIoT and Cyber Security adoption with pragmatic consultancy, implementation and digital solutions.";

  // Default structured data
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AIVHY",
    "url": baseUrl,
    "logo": `${baseUrl}/images/logos/LOGO_AIVHY.webp`,
    "sameAs": [LANGUAGE_DOMAINS.en, LANGUAGE_DOMAINS.nl],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": ["English", "Dutch"],
      "areaServed": ["Europe", "Malta", "Netherlands"]
    },
    "address": [
      {
        "@type": "PostalAddress",
        "addressCountry": "MT",
        "addressRegion": "Malta"
      },
      {
        "@type": "PostalAddress",
        "addressCountry": "NL",
        "addressRegion": "Netherlands"
      }
    ],
    "areaServed": {
      "@type": "Place",
      "name": "Europe"
    }
  };

  // Website structured data
  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AIVHY",
    "url": baseUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const allStructuredData = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [organizationData, websiteData];
  const jsonLdString = safeJsonString(allStructuredData);

  const hreflangLinks = Object.entries(LANGUAGE_DOMAINS).map(([lang, domain]) => ({
    lang,
    href: `${domain}${normalizedPath}`
  }));

  return (
    <>
      {/* Basic Meta Tags */}
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="author" content="AIVHY" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Hreflang for multilingual support */}
      {hreflangLinks.map((l) => (
        <link key={l.lang} rel="alternate" hrefLang={l.lang} href={l.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${LANGUAGE_DOMAINS.en}${normalizedPath}`} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="AIVHY" />
      <meta property="og:locale" content={currentLang === 'nl' ? 'nl_NL' : 'en_US'} />
      <meta property="og:locale:alternate" content={currentLang === 'nl' ? 'en_US' : 'nl_NL'} />
      
      {/* Article-specific Open Graph (only if type is article) */}
      {type === 'article' && (
        <>
          <meta property="article:author" content="AIVHY" />
          <meta property="article:section" content="Technology" />
          <meta property="article:tag" content="Industry 4.0,IIoT,Cyber Security,Digitalization" />
        </>
      )}
      
      {/* Language and Geo Tags */}
      <meta name="language" content={currentLang} />
      <meta name="geo.region" content="MT;NL;EU" />
      <meta name="geo.placename" content="Malta;Netherlands;Europe" />
      
      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {jsonLdString}
      </script>
      
      {children}
    </>
  );
}